import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { founderAdminApi } from '@/lib/founderApi';
import { errMsg, formatPrice } from '@/lib/digitalApi';
import { EntitlementDrawer, fmt, STATUS_CLS } from './founder/EntitlementDrawer';
import { TestCheckoutCard } from './founder/TestCheckoutCard';
import { SaleModeCard } from './founder/SaleModeCard';
import { LiveTestCard } from './founder/LiveTestCard';
import { FulfillmentIssues } from './founder/FulfillmentIssues';

const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-[#eae4d8] rounded-xl p-4" data-testid={testid}>
    <div className="text-[10px] tracking-[0.22em] uppercase text-[#888]">{label}</div>
    <div className="mt-2 text-2xl" style={serif}>{value}</div>
  </div>
);

export default function FounderBlueprintAdmin() {
  const [ov, setOv] = useState(null);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    founderAdminApi.overview().then(setOv).catch((e) => toast.error(errMsg(e)));
    founderAdminApi.entitlements(status ? { status } : {}).then((r) => setItems(r.items)).catch((e) => toast.error(errMsg(e)));
  }, [status, tick]);

  return (
    <div data-testid="founder-blueprint-admin">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#888]">ORIGENNE · Emerging Founder Collection</div>
      <h1 className="mt-2 text-5xl" style={serif}>{ov?.config.title || 'ORIGENNE Founder Blueprint Builder™'}</h1>
      {ov && (
        <>
          <div className="mt-4 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.16em]">
            <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid="fb-sale-mode">
              {ov.config.sale_mode === 'live' ? 'On sale' : 'Coming soon · not for public purchase'}
            </span>
            <span className="px-3 py-1 rounded-full bg-white border border-[#eae4d8]" data-testid="fb-price">{formatPrice(ov.config.price_cents)} one-time</span>
            <span className="px-3 py-1 rounded-full bg-white border border-[#eae4d8]" data-testid="fb-stripe-mode">Stripe {ov.stripe_test_mode ? 'test mode' : 'live mode'}</span>
            <span className="px-3 py-1 rounded-full bg-white border border-[#eae4d8]" data-testid="fb-stage-state">Stages open: {ov.stages_available.length ? ov.stages_available.join(', ') : 'none'}</span>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Customers" value={ov.customers} testid="fb-stat-customers" />
            <Stat label="Active access" value={ov.entitlements.active || 0} testid="fb-stat-active" />
            <Stat label="Revoked" value={ov.entitlements.revoked || 0} testid="fb-stat-revoked" />
            <Stat label="Refunded" value={ov.entitlements.refunded || 0} testid="fb-stat-refunded" />
            <Stat label="Fulfillment issues" value={ov.fulfillment_issues} testid="fb-stat-issues" />
          </div>
          <SaleModeCard mode={ov.config.sale_mode} onChanged={reload} />
          <LiveTestCard status={ov.live_test_status} onChanged={reload} />
          {ov.stripe_test_mode && <TestCheckoutCard />}
        </>
      )}
      <FulfillmentIssues tick={tick} onChanged={reload} />
      <div className="mt-12 flex items-center justify-between">
        <h2 className="text-3xl" style={serif}>Customer access</h2>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-[#eae4d8] rounded-full px-4 py-2 text-sm bg-white" data-testid="fb-status-filter">
          <option value="">All</option><option value="active">Active</option><option value="revoked">Revoked</option><option value="refunded">Refunded</option>
        </select>
      </div>
      <div className="mt-4 bg-white border border-[#eae4d8] rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="fb-entitlements-table">
          <thead><tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#888]">
            <th className="p-4">Customer</th><th className="p-4">Access</th><th className="p-4">Order</th><th className="p-4">Granted</th><th className="p-4">Last sign-in</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[#888]" data-testid="fb-entitlements-empty">No purchases yet.</td></tr>}
            {items.map((e) => (
              <tr key={e.id} onClick={() => setOpen(e.id)} className="border-t border-[#eae4d8] cursor-pointer hover:bg-[#FAF8F5]" data-testid={`fb-entitlement-row-${e.id}`}>
                <td className="p-4"><div>{e.first_name || '—'}</div><div className="text-xs text-[#888]">{e.email}</div></td>
                <td className={`p-4 uppercase text-xs tracking-[0.14em] ${STATUS_CLS[e.status]}`}>{e.status}</td>
                <td className="p-4 text-xs">{e.order_status} · {formatPrice(e.amount_cents)}</td>
                <td className="p-4 text-xs">{fmt(e.granted_at)}</td>
                <td className="p-4 text-xs">{fmt(e.last_login_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <EntitlementDrawer id={open} onClose={() => setOpen(null)} onChanged={reload} />}
    </div>
  );
}
